// /admin/fallbacks/$id — operator detail page for a fallback letter.
//
// Layout (Stripe Radar adapted, NOT side-by-side):
//   - Header: bond context (Ember name + element + relationship state)
//   - Verifier violations: chips with severity + type + span
//   - Triggering user letter: the reply that caused this generation
//   - Fallback body: what the user actually received (always visible)
//   - Rejected draft: the LLM's last failed attempt, collapsed by
//     default behind a "Show rejected draft" disclosure (debugging
//     aid, not the operator's primary focus)
//   - Actions: Re-run pipeline (spawns a new pg-boss job, leaves this
//     row as audit) + Mark reviewed
//
// Re-run + Mark reviewed commit inline. No modal — they're idempotent
// (re-run) or trivially reversible (mark reviewed = a flag flip).

import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import {
  type AdminFallbackDetail,
  type VerifierViolation,
  VERIFIER_SEVERITY,
  getAdminFallback,
  markFallbackReviewed,
  reenqueueFallback,
} from '../lib/api';

export const Route = createFileRoute('/admin/fallbacks/$id')({
  loader: async ({ params }) => {
    const fallback = await getAdminFallback(params.id);
    return { fallback };
  },
  component: FallbackDetailPage,
});

function FallbackDetailPage() {
  const initial = Route.useLoaderData();
  const router = useRouter();
  const [showRejected, setShowRejected] = useState(false);
  const [busy, setBusy] = useState<'reenqueue' | 'mark' | null>(null);

  const f = initial.fallback;

  async function handleReenqueue() {
    setBusy('reenqueue');
    try {
      await reenqueueFallback(f.id);
      await router.invalidate();
    } finally {
      setBusy(null);
    }
  }

  async function handleMarkReviewed() {
    setBusy('mark');
    try {
      await markFallbackReviewed(f.id);
      await router.invalidate();
    } finally {
      setBusy(null);
    }
  }

  const violations = f.verifierResult?.violations ?? [];
  const lastDraft = f.verifierResult?.lastDraft ?? null;

  return (
    <section>
      <Link
        to="/admin/fallbacks"
        className="mb-6 inline-block font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em] transition hover:text-ash-200"
      >
        ← Fallbacks
      </Link>

      <header className="mb-6 border-paper-800/60 border-b pb-4">
        <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em]">
          {f.letterType} fallback
        </p>
        <h2 className="mt-1 font-display text-ash-50 text-2xl">
          {f.bond.emberName} <span className="text-ash-500 text-base">— {f.bond.primaryElement}</span>
        </h2>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
          <span>generated {new Date(f.generatedAt).toISOString().slice(0, 16).replace('T', ' ')}</span>
          {f.retryCount > 0 && <span>retries: {f.retryCount}</span>}
          {f.bond.relationshipState && <span>state: {f.bond.relationshipState}</span>}
          {f.adminReviewedAt && (
            <span className="text-ash-300">
              reviewed {new Date(f.adminReviewedAt).toISOString().slice(0, 16).replace('T', ' ')}
            </span>
          )}
        </div>
      </header>

      {violations.length > 0 && <ViolationsBlock violations={violations} />}

      {f.triggeringUserLetter && (
        <div className="mb-6">
          <p className="mb-2 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
            Triggering user letter
          </p>
          <p className="rounded-sm border border-paper-800/50 bg-paper-900/20 p-4 font-body text-ash-300 text-sm italic leading-relaxed">
            {f.triggeringUserLetter.body}
          </p>
        </div>
      )}

      <div className="mb-6">
        <p className="mb-2 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
          What the bond received (fallback)
        </p>
        <article className="whitespace-pre-line rounded-sm border border-accent/30 bg-paper-900/40 p-4 font-body text-ash-100 text-sm leading-relaxed">
          {f.body}
        </article>
      </div>

      {lastDraft && (
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setShowRejected((v) => !v)}
            className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em] transition hover:text-ash-200"
          >
            {showRejected ? 'Hide rejected draft ▴' : 'Show rejected draft ▾'}
          </button>
          {showRejected && (
            <article className="mt-2 whitespace-pre-line rounded-sm border border-paper-800/50 bg-paper-950/40 p-4 font-body text-ash-400 text-sm leading-relaxed">
              {lastDraft}
            </article>
          )}
        </div>
      )}

      <footer className="flex flex-wrap items-center gap-3 border-paper-800/60 border-t pt-4">
        <button
          type="button"
          onClick={handleReenqueue}
          disabled={busy !== null || !f.triggeringUserLetter}
          className="rounded-sm border border-accent/50 bg-paper-900/60 px-4 py-2 font-display text-ash-100 text-xs uppercase tracking-[0.32em] transition hover:border-accent-strong/80 hover:bg-paper-700/60 disabled:cursor-not-allowed disabled:opacity-50"
          title={!f.triggeringUserLetter ? 'No triggering user letter — cannot re-enqueue' : ''}
        >
          {busy === 'reenqueue' ? 'Re-running…' : 'Re-run pipeline'}
        </button>
        {!f.adminReviewedAt && (
          <button
            type="button"
            onClick={handleMarkReviewed}
            disabled={busy !== null}
            className="rounded-sm border border-paper-800/60 bg-paper-900/40 px-4 py-2 font-display text-ash-300 text-xs uppercase tracking-[0.32em] transition hover:border-ash-700 hover:text-ash-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy === 'mark' ? 'Marking…' : 'Mark reviewed'}
          </button>
        )}
      </footer>
    </section>
  );
}

function ViolationsBlock({ violations }: { violations: VerifierViolation[] }) {
  return (
    <div className="mb-6">
      <p className="mb-2 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
        Verifier violations
      </p>
      <ul className="space-y-2">
        {violations.map((v, idx) => {
          const severity = VERIFIER_SEVERITY[v.code] ?? 'medium';
          return (
            <li
              key={`${v.code}-${idx}`}
              className="flex flex-col gap-1 rounded-sm border border-paper-800/50 bg-paper-900/20 p-3 sm:flex-row sm:items-baseline sm:gap-4"
            >
              <span
                className={`font-display text-[0.6rem] uppercase tracking-[0.32em] ${
                  severity === 'high'
                    ? 'text-accent-strong'
                    : severity === 'medium'
                      ? 'text-accent'
                      : 'text-ash-500'
                }`}
              >
                {severity} · {v.code}
              </span>
              {v.reason && (
                <span className="font-body text-ash-200 text-sm italic">{v.reason}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
